import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TrainingService {
  private baseURL = environment.baseUrl;

  constructor(private http: HttpClient) {}

  // Get all active courses
  getCourses(): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/training/courses');
  }

  // Get course by ID with activities
  getCourseById(id: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/training/courses/' + id);
  }

  // Get enrollments
  getEnrollments(employeeId?: number, managerId?: number): Observable<any> {
    let params = new HttpParams();
    if (employeeId) {
      params = params.set('employeeId', employeeId.toString());
    }
    if (managerId) {
      params = params.set('managerId', managerId.toString());
    }
    return this.http.get(this.baseURL + '/rhAPI/training/enrollments', { params });
  }

  // Get enrollment by ID
  getEnrollmentById(id: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/training/enrollments/' + id);
  }

  // Get signoff roles for an employee (for role-based signoff: who can sign which activities)
  getSignoffRolesForEmployee(employeeId: number): Observable<any> {
    return this.http.get(this.baseURL + '/rhAPI/training/signoff-roles/' + employeeId);
  }

  // Create enrollment
  createEnrollment(employeeId: number, courseId: number, createdByEmployeeId?: number): Observable<any> {
    const body = {
      employeeId: employeeId,
      courseId: courseId,
      createdByEmployeeId: createdByEmployeeId || employeeId
    };
    return this.http.post(this.baseURL + '/rhAPI/training/enrollments', body);
  }

  // Complete an activity (sign-off). signedOffByEmployeeId = current user from auth; backend looks up signer initials from employees3.
  completeActivity(enrollmentId: number, activityId: number, signedOffByEmployeeId: number, comment?: string): Observable<any> {
    const body = {
      signedOffByEmployeeId: signedOffByEmployeeId,
      comment: comment || null
    };
    return this.http.post(
      this.baseURL + '/rhAPI/training/enrollments/' + enrollmentId + '/activities/' + activityId + '/complete',
      body
    );
  }

  // Get manager dashboard (or all employees when viewAll=true for Admin)
  getDashboard(managerId: number | null, viewAll?: boolean): Observable<any> {
    let params = new HttpParams();
    if (viewAll) {
      params = params.set('viewAll', 'true');
    } else if (managerId != null) {
      params = params.set('managerId', managerId.toString());
    }
    return this.http.get(this.baseURL + '/rhAPI/training/dashboard', { params });
  }

  // Complete enrollment (mark course as completed)
  completeEnrollment(enrollmentId: number): Observable<any> {
    return this.http.post(
      this.baseURL + '/rhAPI/training/enrollments/' + enrollmentId + '/complete',
      {}
    );
  }

  createTrainingNotification(employeeId: number, employeeName: string, message?: string): Observable<any> {
    return this.http.post(this.baseURL + '/rhAPI/training/notifications', {
      employeeId,
      employeeName,
      message: message || undefined
    });
  }

  getTrainingNotifications(managerId?: number): Observable<any> {
    let params = new HttpParams();
    if (managerId != null) {
      params = params.set('managerId', managerId.toString());
    }
    return this.http.get(this.baseURL + '/rhAPI/training/notifications', { params });
  }

  markNotificationRead(notificationId: number): Observable<any> {
    return this.http.patch(this.baseURL + '/rhAPI/training/notifications/' + notificationId + '/read', {});
  }

  markAllNotificationsRead(): Observable<any> {
    return this.http.patch(this.baseURL + '/rhAPI/training/notifications/read-all', {});
  }
}
